import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { ZodSchema } from "zod";
import { NextResponse } from "next/server";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * A generic safe-parse helper for API routes.
 * Returns the validated data or a NextResponse error.
 *  Works for JSON or FormData payloads.
 */
export async function validateRequest<T>(
  schema: ZodSchema<T>,
  data: unknown,
): Promise<{ success: true; data: T } | { success: false; response: NextResponse }> {
  const result = schema.safeParse(data);

  if (!result.success) {
    const response = NextResponse.json(
      {
        success: false,
        message: "Invalid request data",
        errors: result.error.flatten().fieldErrors,
      },
      { status: 400 },
    );
    return { success: false, response };
  }

  return { success: true, data: result.data };
}

/**
 * Validates request data with generic error messages to prevent information leakage.
 * Use this for public-facing APIs where specific validation errors could be exploited.
 */
export async function validateRequestWithGenericErrors<T>(
  schema: ZodSchema<T>,
  data: unknown,
): Promise<{ success: true; data: T } | { success: false; response: NextResponse }> {
  const result = schema.safeParse(data);

  if (!result.success) {
    // Return generic error message to prevent information leakage
    const response = NextResponse.json(
      {
        success: false,
        message: "Invalid request data. Please check your input and try again.",
      },
      { status: 400 },
    );
    return { success: false, response };
  }

  return { success: true, data: result.data };
}

/**
 * Generate a random 10-character alphanumeric password with special characters
 */
export function generateRandomPassword(): string {
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const numbers = "0123456789";
  const special = "!@#$%^&*";
  const allChars = lowercase + uppercase + numbers + special;

  // Ensure at least one character from each category
  let password = "";
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += special[Math.floor(Math.random() * special.length)];

  // Fill the rest randomly
  for (let i = password.length; i < 10; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  // Shuffle the password
  return password
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
}

/**
 * Extract client IP address from request headers
 * Checks x-forwarded-for (first IP if comma-separated) and x-real-ip as fallback
 */
export function getClientIp(req: Request | { headers: { get: (name: string) => string | null } }): string {
  const forwardedFor = req.headers.get("x-forwarded-for");
  if (forwardedFor) {
    const firstIp = forwardedFor.split(",")[0]?.trim();
    if (firstIp) return firstIp;
  }
  const realIp = req.headers.get("x-real-ip");
  if (realIp) return realIp;
  return "unknown";
}